import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVerticalIcon, ImageIcon } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/cms/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/utils';
import { Overline } from '@/components/ui/text';
import { Tile } from '@/components/ui/tile';
import { VERSION_STATUS_LABEL } from '../versionOptions';
import type { ProjectSceneTarget, PublishVersionOption } from '../types';
import { shownUrl } from '@/utils/localCopies';

interface Props {
  scenes: ProjectSceneTarget[];
  selection: Record<string, string>;
  onToggleScene: (sceneOriginId: string) => void;
  onSelectVersion: (sceneOriginId: string, sceneInstanceId: string) => void;
  onReorder: (orderedSceneOriginIds: string[]) => void;
}

function versionText(v: PublishVersionOption): string {
  const status = v.disabledReason ?? VERSION_STATUS_LABEL[v.status];
  return v.versionNumber != null ? `v${v.versionNumber} · ${status}` : status;
}

// So the version text is identical (and stays put) across dropdown / static states, and with the project share dialog's rows
export const VERSION_TEXT = 'font-lureo-font-ui text-lureo-13 leading-none';
export const VERSION_BOX = 'flex h-8 w-full max-w-52 items-center rounded-lureo-radius border-lureo-border-width px-2.5';

/** A story row's glyph: its number and cover. Shared with the project share dialog. */
export function StoryGlyph({
  order,
  coverImageUrl,
  highlighted = false,
}: {
  order: number | null;
  coverImageUrl?: string;
  highlighted?: boolean;
}) {
  return (
    <span className="flex items-center gap-2.5">
      <span
        aria-hidden
        className={cn(
          'w-5 shrink-0 text-center font-lureo-font-ui text-lureo-16 font-bold tabular-nums leading-none',
          highlighted ? 'text-lureo-brand-ink' : 'text-lureo-ink-subtlest',
        )}
      >
        {order ?? '–'}
      </span>

      <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lureo-radius-lg border-lureo-border-width border-lureo-line bg-lureo-neutral">
        {coverImageUrl ? (
          <img src={shownUrl(coverImageUrl)} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-lureo-ink-subtlest">
            <ImageIcon className="h-5 w-5" />
          </span>
        )}
      </span>
    </span>
  );
}

interface RowProps {
  scene: ProjectSceneTarget;
  order: number | null;
  chosen: string | undefined;
  onToggle: () => void;
  onSelectVersion: (sceneInstanceId: string) => void;
}

function SceneRow({ scene, order, chosen, onToggle, onSelectVersion }: RowProps) {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: scene.sceneOriginId,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const included = chosen != null;
  const publishable = scene.versions.filter((v) => v.selectable && v.anchorCategory !== 'place_anywhere');
  const canPublish = publishable.length > 0;
  // The scene's only publishable version(s) use Place Anywhere, so it has no
  // anchor and can't be submitted for review.
  const blockedByPlaceAnywhere =
    !canPublish && scene.versions.some((v) => v.selectable && v.anchorCategory === 'place_anywhere');
  const hasChoice = publishable.length > 1;
  const previewVersion =
    scene.versions.find((v) => v.sceneInstanceId === chosen) ?? publishable[0] ?? scene.versions[0];

  const versionAndCheck = (
    <div className="flex min-w-0 flex-1 items-center">
      <div
        className="flex min-w-0 flex-col gap-1.5"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Overline size="label" as="span">
          Version
        </Overline>
        {!canPublish ? (
          <div className={cn(VERSION_BOX, VERSION_TEXT, 'border-transparent text-lureo-ink-subtlest')}>
            {blockedByPlaceAnywhere
              ? 'Needs an anchor'
              : (scene.versions[0]?.disabledReason ?? 'Nothing to publish')}
          </div>
        ) : included && hasChoice ? (
          <Select value={chosen} onValueChange={onSelectVersion}>
            <SelectTrigger size="sm" className="w-full max-w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent sideOffset={4}>
              {scene.versions.map((v) => (
                <SelectItem
                  key={v.sceneInstanceId}
                  value={v.sceneInstanceId}
                  disabled={!v.selectable}
                >
                  {versionText(v)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className={cn(VERSION_BOX, VERSION_TEXT, 'border-transparent text-lureo-ink-subtle')}>
            {previewVersion ? versionText(previewVersion) : ''}
          </div>
        )}
      </div>

      {/* The row is the toggle; the checkbox only shows its state */}
      <Checkbox
        checked={included}
        disabled={!canPublish}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none ml-auto"
      />
    </div>
  );

  return (
    <li ref={setNodeRef} style={style} className={cn('relative', isDragging && 'z-10')}>
      {/* A div: the row holds the version select; the sortable gives it its role and keys */}
      <Tile
        as="div"
        layout="split"
        {...attributes}
        {...listeners}
        onClick={() => canPublish && onToggle()}
        selected={included}
        disabled={!canPublish}
        className={cn('select-none', canPublish && 'cursor-grab active:cursor-grabbing', isDragging && 'shadow-lureo-shadow-lift')}
        glyph={
          <span className="flex items-center gap-2.5">
            <DotsSixVerticalIcon aria-hidden className="h-5 w-5 text-lureo-ink-subtlest" />
            <StoryGlyph order={order} coverImageUrl={scene.coverImageUrl} highlighted={included} />
          </span>
        }
        label={scene.sceneOriginName || 'Untitled story'}
        trailing={
          blockedByPlaceAnywhere ? (
            <Tooltip>
              <TooltipTrigger asChild>{versionAndCheck}</TooltipTrigger>
              <TooltipContent side="top" sideOffset={8}>
                This story uses Place Anywhere. Add an anchor to publish it.
              </TooltipContent>
            </Tooltip>
          ) : (
            versionAndCheck
          )
        }
      />
    </li>
  );
}

export function SelectScenesStep({ scenes, selection, onToggleScene, onSelectVersion, onReorder }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (scenes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="font-lureo-font-prose text-lureo-15 text-lureo-ink-subtle leading-normal text-center max-w-sm">
          This project doesn't have any stories yet.
        </p>
      </div>
    );
  }

  const ordered = [...scenes].sort((a, b) => a.orderNumber - b.orderNumber);
  const orderedIds = ordered.map((s) => s.sceneOriginId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(active.id as string);
    const newIndex = orderedIds.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(orderedIds, oldIndex, newIndex));
  };

  return (
    <TooltipProvider delayDuration={200}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
          <ol className="space-y-2">
          {(() => {
            let publishPos = 0;
            return ordered.map((scene) => {
              const chosen = selection[scene.sceneOriginId];
              const order = chosen != null ? ++publishPos : null;
              return (
                <SceneRow
                  key={scene.sceneOriginId}
                  scene={scene}
                  order={order}
                  chosen={chosen}
                  onToggle={() => onToggleScene(scene.sceneOriginId)}
                  onSelectVersion={(sceneInstanceId) => onSelectVersion(scene.sceneOriginId, sceneInstanceId)}
                />
              );
            });
          })()}
          </ol>
        </SortableContext>
      </DndContext>
    </TooltipProvider>
  );
}
